import { faStopwatch } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'components/Button'
import { Col, Row } from 'components/Ui-Kits/Grid'
import { Heading, Text } from 'components/Ui-Kits/Text'
import { useAppTranslation } from 'i18n/hooks'
import { number, object } from 'prop-types'
import styled from 'styled-components'
import { useCallback, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { getMinimumRise } from 'helpers/getMinimumRise'
import { createUserLiveBid } from 'cloudFunctions'
import { auctionStatuses } from 'constants/publishedProduct'
import { LastBid } from './LastBid'
import { useMyLastLiveBid } from 'hooks/data/auction/useMyLastLiveBid'
import { CountdownWrap } from './CountdownWrap'
import { useProfile } from 'hooks/useProfile'
import { useGetTranslated } from 'hooks/useGetTranslated'
import { ItemMessage } from './ItemMessage'

export const CurrentItem = ({ product, auction, total }) => {
  const { t } = useAppTranslation()
  const getTranslated = useGetTranslated()
  const { bid } = useMyLastLiveBid(auction?.id, product?.id)
  const [secondsPassed, setSecondsPassed] = useState(0)
  const { user, profile } = useProfile()

  const bidAmount = useMemo(() => {
    if (!product.secondHighestBid) return product.technicalDetails.AuctionMinimumPrice

    const amount = product.secondHighestBid

    return amount + getMinimumRise(amount)
  }, [product])

  const [loading, setLoading] = useState(false)

  const setRemainingSeconds = useCallback(() => {
    const secondsPassed = Math.min(dayjs().diff(auction.auctionTimerStartAt, 'seconds'), auction.timerDuration)
    setSecondsPassed(secondsPassed)

    return secondsPassed
  }, [auction.timerDuration, auction.auctionTimerStartAt])

  const secondsLeft = useMemo(() => auction.timerDuration - secondsPassed, [secondsPassed, auction.timerDuration])
  const progress = useMemo(
    () => Math.min((secondsLeft * 100) / auction.timerDuration, 100),
    [secondsLeft, auction.timerDuration]
  )

  useEffect(() => {
    if (auction.pauseMessage || product?.auctionStatus !== auctionStatuses.CURRENT) return
    const passed = setRemainingSeconds()
    if (passed >= auction.timerDuration) return

    const interval = setInterval(() => {
      const passed = setRemainingSeconds()
      if (passed >= auction.timerDuration) clearInterval(interval)
    }, 500)

    return () => clearInterval(interval)
  }, [setRemainingSeconds, auction.timerDuration, auction.pauseMessage, product?.auctionStatus])

  const blurry =
    !auction.liveAuctionStartedAt ||
    (product.auctionStatus !== auctionStatuses.CURRENT && !auction.liveAuctionEndedAt && !!auction.liveAuctionStartedAt)

  return (
    <Wrap blurry={blurry}>
      <Row>
        <Col col={6} sm={12}>
          <Image src={product.featuredImage?.url} />
          <Text size={1}>
            {t('lotNumber')} {product.sort + 1} / {total}
          </Text>
          <Heading color="primary" mt={0.85} size={1.25}>
            {getTranslated(product.name)}
          </Heading>
        </Col>
        <Col col={6} sm={12}>
          <Background>
            <LastBid
              auctionId={auction.id}
              productId={product.id}
              amount={product.secondHighestBid || product?.technicalDetails?.AuctionMinimumPrice}
              hasBids={!!product.secondHighestBid}
            />
            <TextWrap align="center">
              <Text color="primary" bold aria-setsize={1.375}>
                {t('startingBid')}
              </Text>
              <Text color="primary" bold aria-setsize={1.375}>
                {product.technicalDetails.AuctionMinimumPrice.toLocaleString('fi-FI')} €
              </Text>
            </TextWrap>
            <Button
              color="primary"
              fullWidth
              onClick={async () => {
                if (!user) {
                  return alert(t('pleaseLoginOrRegisterToBid'), 'danger')
                }
                if (!profile?.verified) {
                  return alert(t('verifyAccountToBid'), 'danger')
                }

                if (bidAmount > profile.maxBiddingLevel && profile.maxBiddingLevel != -1) {
                  return alert(t('verifyAccountToBidMoreThenAmount', { amount: profile.maxBiddingLevel }), 'danger')
                }

                setLoading(true)
                await createUserLiveBid({ itemId: product.id, auctionId: auction.id, amount: bidAmount })
                setLoading(false)
              }}
              disabled={product.auctionStatus !== auctionStatuses.CURRENT || loading || !!bid?.isWinning}
            >
              {t('bidButtonLabel', { minimumBid: bidAmount.toLocaleString('fi-FI') })}
            </Button>
          </Background>
          <TextWrap noMargin>
            <IconTextWrap>
              <Icon icon={faStopwatch} />
              <Text color="primary" bold>
                {t('timeToMakeOffer')}
              </Text>
            </IconTextWrap>
            <Text color="primary" bold>
              {t('nSeconds', { n: secondsLeft })}
            </Text>
          </TextWrap>
          <ProgressBar progress={progress} />
        </Col>
      </Row>
      {!auction.liveAuctionStartedAt && <CountdownWrap startDate={auction.startDate} />}
      {product.auctionStatus !== auctionStatuses.CURRENT &&
        !auction.liveAuctionEndedAt &&
        !!auction.liveAuctionStartedAt && <ItemMessage success={!!bid?.isWinning} />}
    </Wrap>
  )
}

CurrentItem.propTypes = {
  auction: object.isRequired,
  product: object.isRequired,
  total: number,
}

const Image = styled.img`
  width: 100%;
  margin-bottom: 1.5rem;
`
const Wrap = styled.div`
  width: 100%;
  margin-bottom: 1rem;
  box-sizing: border-box;
  border: 2px solid var(--color-primary);
  padding: 0.87rem 1.87rem;
  position: relative;
  @media screen and (min-width: 760px) {
    width: 66%;
    margin-bottom: 0rem;
  }
  & > ${Row} {
    ${({ blurry }) => blurry && 'filter: blur(5px);'}
  }
`

const TextWrap = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: ${({ justify = 'space-between' }) => justify};
  align-items: ${({ align = 'flex-start' }) => align};
  margin-bottom: ${({ noMargin }) => !noMargin && '1rem'};
`

const IconTextWrap = styled(Text)`
  flex: 1;
`
const Background = styled.div`
  background-color: var(--color-grey);
  box-sizing: border-box;
  padding: 1rem;
  margin-bottom: 1.75rem;
`
const ProgressBar = styled.div`
  height: 0.75rem;
  width: 100%;
  background-color: var(--color-primary-lighter);
  &:before {
    background-color: var(--color-primary);
    height: 100%;
    content: '';
    display: block;
    width: ${({ progress = 0 }) => progress}%;
  }
`

const Icon = styled(FontAwesomeIcon)`
  color: var(--color-primary);
  font-size: 1rem;
  margin-right: 0.375rem;
`
